import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "./firebase"; // make sure your firebase.js exports `db` properly
import emailjs from "emailjs-com";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import "./onlineappointment.css";

const Notification = ({ message, type, onClose }) => {
  return (
    <div className={`notification ${type}`} onClick={onClose}>
      {message}
    </div>
  );
};

function OnlineAppointment() {
  const [greeting, setGreeting] = useState("");
  const [userName, setUserName] = useState("");
  const [selectedTest, setSelectedTest] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [step, setStep] = useState(1);
  const [notification, setNotification] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [reservedSlots, setReservedSlots] = useState([]);

  const currentDate = new Date();

  // Greeting & generate time slots
  useEffect(() => {
    setGreeting(new Date().getHours() < 12 ? "Good Morning" : "Good Evening");

    const slots = [];
    const start = new Date();
    start.setHours(8, 30, 0, 0);
    const end = new Date();
    end.setHours(19, 0, 0, 0);

    while (start < end) {
      slots.push(
        new Date(start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
      start.setMinutes(start.getMinutes() + 30);
    }
    setTimeSlots(slots);
  }, []);

  // Fetch reserved slots for selected date
  useEffect(() => {
    const fetchReservedSlots = async () => {
      if (!appointmentDate) return;
      try {
        const q = query(collection(db, "appointments"), where("date", "==", appointmentDate));
        const querySnapshot = await getDocs(q);
        const slots = querySnapshot.docs.map((doc) => doc.data().time);
        setReservedSlots(slots);
      } catch (err) {
        setNotification({ message: "Failed to fetch reserved slots.", type: "error" });
      }
    };
    fetchReservedSlots();
  }, [appointmentDate]);

  const handleTestSelection = (test) => {
    setSelectedTest(test);
    setStep(1.5);
  };

  const handleNameInput = () => {
    if (userName.trim() === "") {
      setNotification({ message: "Please enter your name.", type: "error" });
      return;
    }
    setStep(2);
  };

  const handleDateChange = (e) => {
    const selected = e.target.value;
    if (selected < currentDate.toISOString().split("T")[0]) {
      setNotification({ message: "Please select today or future date.", type: "error" });
      return;
    }
    setAppointmentDate(selected);
    setStep(3);
  };

  const handleTimeSelection = (time) => {
    if (reservedSlots.includes(time)) {
      setNotification({ message: "Slot already reserved.", type: "error" });
      return;
    }
    setAppointmentTime(time);
    setStep(4);
  };

  // Simple phone number validation and proceed to confirmation
  const handlePhoneNumber = () => {
    if (!phoneNumber || !/^\+\d{10,15}$/.test(phoneNumber)) {
      setNotification({ 
        message: "Please enter a valid phone number with country code (e.g., +94123456789).", 
        type: "error" 
      });
      return;
    }
    // Phone number is valid, proceed to confirmation
    setStep(5);
  };

  const confirmAppointment = async () => {
    if (!selectedTest || !appointmentDate || !appointmentTime || !userName || !phoneNumber) {
      setNotification({ message: "Please fill all fields before confirming.", type: "error" });
      return;
    }

    try {
      // Save to Firebase
      await addDoc(collection(db, "appointments"), {
        test: selectedTest,
        date: appointmentDate,
        time: appointmentTime,
        name: userName,
        phone: phoneNumber,
        createdAt: new Date(),
      });

      // Send confirmation email
      await emailjs.send(
        "service_g4q1q4f",
        "template_7h7lyi5",
        {
          user_name: userName,
          test_name: selectedTest,
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          phone_number: phoneNumber,
        },
        "zM40ykd4Fa6c4SUtE"
      );

      setStep(6);
      setNotification({ 
        message: "Appointment confirmed successfully! A confirmation email has been sent.", 
        type: "success" 
      });
    } catch (err) {
      console.error("Error confirming appointment:", err);
      setNotification({ 
        message: "Error confirming appointment. Please try again or contact us directly.", 
        type: "error" 
      });
    }
  };

  const renderTimeSlots = () =>
    timeSlots.map((slot, i) => {
      const [h, m] = slot.split(":").map(Number);
      const slotTime = new Date();
      slotTime.setHours(h, m, 0, 0);

      const isPast = appointmentDate === currentDate.toISOString().split("T")[0] && slotTime < currentDate;
      const isReserved = reservedSlots.includes(slot);

      return (
        <button
          key={i}
          onClick={() => !isPast && !isReserved && handleTimeSelection(slot)}
          disabled={isPast || isReserved}
          className={`button-appointment ${isPast || isReserved ? "disabled-slot" : ""}`}
        >
          {slot}
        </button>
      );
    });

  return (
    <div className="appointment-container">
      <div className="appointment-box">
        <h2>{greeting}! Welcome to Eye Care Clinic & Optical</h2>

        {step === 1 && (
          <div>
            <p>Select appointment type:</p>
            <div className="test-buttons">
              {["Low Vision", "Eye Clinic Test", "Orthoptic Test", "Eye Investigation", "Eye Therapeutic"].map((t) => (
                <button key={t} className="button-appointment" onClick={() => handleTestSelection(t)}>
                  {t.replace(" Test", "")}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1.5 && (
          <div>
            <p>Enter your full name:</p>
            <input 
              type="text" 
              placeholder="Enter your name" 
              value={userName} 
              onChange={(e) => setUserName(e.target.value)} 
            />
            <div className="navigation-buttons">
              <button className="button-appointment" onClick={() => setStep(1)}>
                Previous
              </button>
              <button className="button-appointment" onClick={handleNameInput}>
                Next
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p>Select appointment date:</p>
            <input 
              type="date" 
              min={currentDate.toISOString().split("T")[0]} 
              onChange={handleDateChange} 
            />
            <div className="navigation-buttons">
              <button className="button-appointment" onClick={() => setStep(1.5)}>
                Previous
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <p>Select a time slot:</p>
            <div className="time-slots">{renderTimeSlots()}</div>
            <div className="navigation-buttons">
              <button className="button-appointment" onClick={() => setStep(2)}>
                Previous
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <p>Enter your phone number for confirmation:</p>
            <PhoneInput
              placeholder="+94765210235"
              defaultCountry="LK"
              value={phoneNumber}
              onChange={setPhoneNumber}
            />
            <div className="navigation-buttons">
              <button className="button-appointment" onClick={() => setStep(3)}>
                Previous
              </button>
              <button className="button-appointment" onClick={handlePhoneNumber}>
                Next
              </button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h3>Confirm Your Appointment</h3>
            <div className="appointment-summary">
              <p><strong>Test Type:</strong> {selectedTest}</p>
              <p><strong>Date:</strong> {appointmentDate}</p>
              <p><strong>Time:</strong> {appointmentTime}</p>
              <p><strong>Name:</strong> {userName}</p>
              <p><strong>Phone:</strong> {phoneNumber}</p>
            </div>
            <div className="navigation-buttons">
              <button className="button-appointment" onClick={() => setStep(4)}>
                Edit Details
              </button>
              <button className="button-appointment confirm-btn" onClick={confirmAppointment}>
                Confirm Appointment
              </button>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="confirmation-screen">
            <h3>✅ Appointment Confirmed!</h3>
            <p>Your appointment has been successfully booked.</p>
            <div className="appointment-details">
              <p><strong>Reference ID:</strong> APT-{Date.now().toString().slice(-6)}</p>
              <p><strong>Test Type:</strong> {selectedTest}</p>
              <p><strong>Date:</strong> {appointmentDate}</p>
              <p><strong>Time:</strong> {appointmentTime}</p>
              <p><strong>Name:</strong> {userName}</p>
            </div>
            <p className="confirmation-note">
              A confirmation email has been sent. Please arrive 10 minutes before your appointment.
            </p>
            <button 
              className="button-appointment" 
              onClick={() => {
                // Reset form for new appointment
                setSelectedTest("");
                setAppointmentDate("");
                setAppointmentTime("");
                setUserName("");
                setPhoneNumber("");
                setStep(1);
              }}
            >
              Book Another Appointment
            </button>
          </div>
        )}

        {notification && (
          <Notification 
            message={notification.message} 
            type={notification.type} 
            onClose={() => setNotification(null)} 
          />
        )}
      </div>
    </div>
  );
}

export default OnlineAppointment;